import clsx from 'clsx';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { safeHttpUrl } from '@/lib/safeUrl';
import type { Message } from '@flock-types/chat.types';

/**
 * Non-text message content: images, GIFs, files, scripture, link previews.
 * Upgraded with lightbox + provider embeds in the media step.
 */
export default function MessageRichContent({
  message,
  own,
  onImageClick,
}: {
  message: Message;
  own: boolean;
  onImageClick?: (url: string) => void;
}) {
  const content = message.content || {};
  const parts: React.ReactNode[] = [];

  const safeMediaUrl = safeHttpUrl(content.mediaUrl);
  const safeGiphyOriginalUrl = safeHttpUrl(content.giphyOriginalUrl);

  if ((message.type === 'image' || message.type === 'gif') && safeMediaUrl) {
    // Reserve layout space from the stored dimensions. Without an explicit
    // size the <img> is 0×0 until loaded, and `loading="lazy"` never fires
    // for 0×0 images inside a scroll container — they stay broken forever.
    const w = content.imageWidth || 320;
    const h = content.imageHeight || 240;
    const ratio = w / h;
    // Cap at 320px in both directions while preserving aspect ratio
    const displayWidth = Math.round(Math.min(w, 320, 320 * ratio));
    parts.push(
      <button
        key="media"
        onClick={() => onImageClick?.(safeGiphyOriginalUrl || safeMediaUrl)}
        className="mb-1 block overflow-hidden rounded-xl"
      >
        <img
          src={safeMediaUrl}
          alt={content.fileName || (message.type === 'gif' ? 'GIF' : 'Image')}
          className="max-w-full object-contain"
          style={{ width: displayWidth, aspectRatio: `${ratio}` }}
        />
      </button>
    );
  }

  if (message.type === 'file' && safeMediaUrl) {
    parts.push(
      <a
        key="file"
        href={safeMediaUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={clsx(
          'mb-1 flex items-center gap-2 rounded-lg border px-3 py-2 text-xs',
          own ? 'border-white/30 text-white' : 'border-flock-gray-200 text-flock-navy-700'
        )}
      >
        <DocumentIcon className="h-5 w-5 shrink-0" />
        <span className="truncate">{content.fileName || 'Attachment'}</span>
      </a>
    );
  }

  if (content.scripture) {
    const s = content.scripture;
    parts.push(
      <div
        key="scripture"
        className={clsx(
          'mb-1 rounded-lg border-l-4 px-3 py-2 text-xs',
          own
            ? 'border-white/50 bg-white/10 text-white'
            : 'border-flock-navy-400 bg-flock-navy-50 text-flock-navy-800'
        )}
      >
        <div className="mb-0.5 font-semibold">
          {s.reference} ({s.translation})
        </div>
        <div className="italic">“{s.text}”</div>
      </div>
    );
  }

  // NOTE: link previews are rendered by MessageBubble via LinkPreviewCard
  // (embeds/) so the bubble can also strip the raw URL from the text —
  // this component handles media-type content only.

  return parts.length > 0 ? <>{parts}</> : null;
}
